import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Plus, Search } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@app/components/ui/popover";
import { cn } from "@app/lib/utils";
import type { PathwayDimension } from "@app/types/dashboard-pathway";

/**
 * Choose an axis to add to a card.
 *
 * Grouped by the ontology's own `group_name`, with the five episode-native axes first: those are the
 * ones the flat index has no column for, so they are what the reader has never been able to ask for.
 * Dimensions already on the card are hidden rather than disabled — a list of things you cannot pick
 * is a longer list, not a clearer one.
 */
export default function DimensionPicker({
  dimensions,
  exclude,
  onPick,
  label,
  disabled,
}: {
  dimensions: PathwayDimension[];
  exclude: string[];
  onPick: (key: string) => void;
  label?: React.ReactNode;
  disabled?: boolean;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const available = dimensions.filter(
      (d) => !exclude.includes(d.key) && (!needle || d.label.toLowerCase().includes(needle)),
    );
    const buckets = new Map<string, PathwayDimension[]>();
    for (const dimension of available) {
      const group = dimension.native ? "__native__" : dimension.group || t`Autres champs`;
      buckets.set(group, [...(buckets.get(group) ?? []), dimension]);
    }
    const native = buckets.get("__native__") ?? [];
    buckets.delete("__native__");
    return [
      ...(native.length ? ([[t`Parcours de soin`, native]] as [string, PathwayDimension[]][]) : []),
      ...([...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0])) as [string, PathwayDimension[]][]),
    ];
  }, [dimensions, exclude, search, t]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1",
            "text-2xs font-medium text-slate-500 transition-colors hover:border-primary/50 hover:text-primary",
            "disabled:cursor-not-allowed disabled:opacity-40",
          )}
        >
          <Plus className="h-3 w-3" />
          {label ?? <Trans>colonne</Trans>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t`Rechercher une dimension...`}
            className="w-full bg-transparent text-xs outline-none placeholder:text-slate-400"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {groups.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-slate-400">
              <Trans>Aucune dimension disponible</Trans>
            </p>
          )}
          {groups.map(([group, items]) => (
            <div key={group} className="py-1">
              <p className="px-3 py-1 text-3xs font-semibold uppercase tracking-wide text-slate-400">
                {group}
              </p>
              {items.map((dimension) => (
                <button
                  key={dimension.key}
                  type="button"
                  onClick={() => {
                    onPick(dimension.key);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-primary/5"
                >
                  <span className="truncate">{dimension.label}</span>
                  <span className="shrink-0 text-3xs uppercase tracking-wide text-slate-400">
                    {dimension.kind}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
